import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Check, Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import InstrumentLogo from "./InstrumentLogo";

// Closed-set instrument picker for Sell: only lists tickers the user actually
// holds (quantity > 0), unlike TickerSearch which searches the whole market.
export default function HoldingSelect({ value, holdings = [], onSelect, testid = "holding-select" }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef();

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const q = query.trim().toLowerCase();
  const filtered = q
    ? holdings.filter((h) => h.ticker.toLowerCase().includes(q) || h.name?.toLowerCase().includes(q))
    : holdings;

  const choose = (h) => { onSelect({ ticker: h.ticker, name: h.name }); setOpen(false); setQuery(""); };
  const current = holdings.find((h) => h.ticker === value);

  return (
    <div ref={ref} className="relative">
      <button type="button" data-testid={testid} onClick={() => setOpen((o) => !o)} className="input flex items-center justify-between text-left">
        <span className="flex items-center gap-2 min-w-0">
          {current ? (
            <>
              <InstrumentLogo logo={current.logo} ticker={current.ticker} size={22} rounded="rounded-md" />
              <span className="text-slate-900 truncate">{current.ticker} · {current.name}</span>
            </>
          ) : (
            <span className="text-slate-400">{t("dialogs.selectHolding")}</span>
          )}
        </span>
        <ChevronDown size={16} className={`text-slate-400 transition-transform shrink-0 ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 4 }} transition={{ duration: 0.15 }}
            className="absolute z-50 mt-2 w-full bg-white rounded-2xl border border-slate-200/70 shadow-soft overflow-hidden"
          >
            <div className="p-2 border-b border-slate-100 relative">
              <Search className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400" size={15} />
              <input autoFocus data-testid="holding-search-input" value={query} onChange={(e) => setQuery(e.target.value)}
                placeholder={t("dialogs.searchHolding")} className="input py-2 pl-8 text-sm" />
            </div>
            <div className="max-h-56 overflow-y-auto py-1">
              {filtered.length === 0 && <div className="px-3 py-3 text-sm text-slate-400">{t("dialogs.noHoldingsFound")}</div>}
              {filtered.map((h) => (
                <button type="button" key={h.ticker} data-testid={`holding-option-${h.ticker}`} onClick={() => choose(h)}
                  className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-brand-50 transition-colors text-left text-sm">
                  <InstrumentLogo logo={h.logo} ticker={h.ticker} size={26} rounded="rounded-md" />
                  <span className="flex-1 min-w-0">
                    <span className="block font-medium text-slate-900">{h.ticker}</span>
                    <span className="block text-xs text-slate-400 truncate">{h.name}</span>
                  </span>
                  {value === h.ticker && <Check size={15} className="text-brand-500 shrink-0" />}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
